import React, { useEffect, useRef, useState } from 'react';
import { GoogleMap, MarkerF } from '@react-google-maps/api';
import { coordinates, getMeetingParticipants, getNearbyMeetingPlaces, getRecommendations, recommendationPath } from '../../services/meetingLocationService';
import './MeetingLocationPicker.css';

const distance = (miles, minutes) => {
  const pieces = [];
  if (miles != null && Number.isFinite(Number(miles))) pieces.push(`${Number(miles).toFixed(2)} mi`);
  if (minutes != null && Number.isFinite(Number(minutes))) pieces.push(`${Math.round(Number(minutes))} min walk`);
  return pieces.join(' · ') || 'Walking estimate unavailable';
};

export default function MeetingLocationPicker({ profile, partnerId, dogId, isMapLoaded, initialCenter, selectedLocation, selectedAddress, onSelect }) {
  const [mode, setMode] = useState(recommendationPath ? 'suggested' : 'manual');
  const [participants, setParticipants] = useState(null);
  const [participantError, setParticipantError] = useState('');
  const [limits, setLimits] = useState({ you: 3, partner: 3 });
  const [appliedLimits, setAppliedLimits] = useState(limits);
  const [venues, setVenues] = useState([]);
  const [nearby, setNearby] = useState([]);
  const [status, setStatus] = useState('idle');
  const [nearbyStatus, setNearbyStatus] = useState('idle');
  const [error, setError] = useState('');
  const [retry, setRetry] = useState(0);
  const [direct, setDirect] = useState(null);
  const [directStatus, setDirectStatus] = useState('idle');
  const [address, setAddress] = useState('');
  const [addressStatus, setAddressStatus] = useState('');
  const [manualCenter, setManualCenter] = useState(null);
  const [locating, setLocating] = useState(false);
  const alive = useRef(true);
  const geocodeVersion = useRef(0);
  const directController = useRef(null);
  const center = coordinates(selectedLocation) || manualCenter || coordinates(profile) || coordinates(initialCenter);
  const profileRef = useRef(profile);
  profileRef.current = profile;

  useEffect(() => {
    alive.current = true;
    return () => { alive.current = false; geocodeVersion.current += 1; directController.current?.abort(); };
  }, []);

  useEffect(() => {
    if (!recommendationPath) return;
    const controller = new AbortController();
    setParticipants(null);
    setParticipantError('');
    getMeetingParticipants(profileRef.current, partnerId, dogId, controller.signal)
      .then((value) => { if (!controller.signal.aborted) setParticipants(value); })
      .catch(() => { if (!controller.signal.aborted) setParticipantError('Suggestions are unavailable for these participants. You can choose a place on the map.'); });
    return () => controller.abort();
  }, [profile?.id, partnerId, dogId, retry]);

  useEffect(() => {
    if (!participants || mode !== 'suggested') return;
    const controller = new AbortController();
    setStatus('loading'); setError(''); setVenues([]);
    const youAreWalker = participants.roles.walker_id === Number(profile?.id);
    const requestLimits = { walker: youAreWalker ? appliedLimits.you : appliedLimits.partner, owner: youAreWalker ? appliedLimits.partner : appliedLimits.you };
    getRecommendations(participants.roles, requestLimits, controller.signal)
      .then(({ venues }) => { if (!controller.signal.aborted) { setVenues(venues); setStatus('ready'); } })
      .catch((err) => {
        if (controller.signal.aborted) return;
        setStatus('error');
        setError(err.response?.data?.code === 'user_not_found'
          ? 'One of these profiles is not available to the suggestion service yet. Choose a place manually.'
          : 'We couldn’t load suggestions. Try again or choose a place manually.');
      });
    return () => controller.abort();
  }, [participants, appliedLimits, mode, profile?.id]);

  // Nearby fallback uses a saved/map point without requiring another permission prompt.
  const nearbyPoint = manualCenter || coordinates(profile) || coordinates(initialCenter);
  const lat = nearbyPoint?.lat; const lng = nearbyPoint?.lng;
  useEffect(() => {
    if (mode !== 'manual' || lat == null || lng == null) return;
    const controller = new AbortController();
    setNearbyStatus('loading'); setNearby([]);
    getNearbyMeetingPlaces({ lat, lng }, controller.signal)
      .then((places) => { if (!controller.signal.aborted) { setNearby(places); setNearbyStatus('ready'); } })
      .catch(() => { if (!controller.signal.aborted) setNearbyStatus('error'); });
    return () => controller.abort();
  }, [mode, lat, lng, retry]);

  const selectVenue = (venue) => {
    geocodeVersion.current += 1;
    setAddressStatus('');
    onSelect({ ...venue, address: venue.address || venue.name });
  };
  const geocode = (request) => {
    if (!isMapLoaded || !window.google?.maps) return;
    const version = ++geocodeVersion.current;
    setAddressStatus('Finding address…');
    // Do not allow a previous address to be submitted while resolving a new pin.
    onSelect({ name: '', address: '' });
    new window.google.maps.Geocoder().geocode(request, (results, resultStatus) => {
      if (!alive.current || version !== geocodeVersion.current) return;
      if (resultStatus !== 'OK' || !results?.[0]) { setAddressStatus('Could not find that address. Try another address or pin.'); return; }
      const result = results[0];
      const point = { lat: result.geometry.location.lat(), lng: result.geometry.location.lng() };
      setManualCenter(point);
      setAddressStatus('');
      // Manual selection clears any previously selected recommendation ID.
      onSelect({ ...point, name: result.formatted_address, address: result.formatted_address });
    });
  };
  const useLocation = () => {
    if (!navigator.geolocation) { setAddressStatus('Location access is unavailable. Search an address instead.'); return; }
    setLocating(true);
    navigator.geolocation.getCurrentPosition((position) => {
      if (!alive.current) return;
      setLocating(false);
      geocode({ location: { lat: position.coords.latitude, lng: position.coords.longitude } });
    }, () => { if (alive.current) { setLocating(false); setAddressStatus('Location access is unavailable. Search an address or tap the map.'); } }, { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 });
  };
  const loadDirect = async () => {
    directController.current?.abort();
    const controller = new AbortController(); directController.current = controller;
    setDirectStatus('loading'); setDirect(null);
    try {
      const data = await getRecommendations(participants.roles, null, controller.signal, true);
      if (controller.signal.aborted) return;
      if (data.mode !== 'direct_meeting' || !data.direct_route) throw new Error('No route');
      setDirect(data.direct_route); setDirectStatus('ready');
    } catch { if (!controller.signal.aborted) setDirectStatus('error'); }
  };
  const youAreWalker = participants?.roles.walker_id === Number(profile?.id);
  const partnerName = participants?.partner?.user_name || 'Other participant';
  const displayed = mode === 'suggested' ? venues : nearby;
  const mapCenter = coordinates(selectedLocation) || coordinates(displayed[0]) || center;

  return <div className="meeting-picker">
    {recommendationPath && <div className="meeting-picker-tabs" aria-label="Location selection method">
      <button type="button" aria-pressed={mode === 'suggested'} onClick={() => setMode('suggested')}>Suggested places</button>
      <button type="button" aria-pressed={mode === 'manual'} onClick={() => setMode('manual')}>Choose on map</button>
    </div>}
    {mode === 'suggested' && <>
      <p className="meeting-picker-note">Suggestions use both participants’ saved profile locations.</p>
      <form className="meeting-picker-limits" onSubmit={(event) => { event.preventDefault(); setAppliedLimits({ ...limits }); if (!participants) setRetry((value) => value + 1); }}>
        <label>Your walking limit (mi)<input type="number" min="0.1" step="0.1" required value={limits.you} onChange={(event) => setLimits({ ...limits, you: event.target.value === '' ? '' : Number(event.target.value) })} /></label>
        <label>{partnerName}’s limit (mi)<input type="number" min="0.1" step="0.1" required value={limits.partner} onChange={(event) => setLimits({ ...limits, partner: event.target.value === '' ? '' : Number(event.target.value) })} /></label>
        <button type="submit" disabled={(!participants && !participantError) || (!!participants && status === 'loading')}>Find places</button>
      </form>
      <div role="status" aria-live="polite">
        {participantError || (!participants ? 'Loading meeting participants…' : status === 'loading' ? 'Finding places for both of you…' : error)}
        {participants && status === 'ready' && !venues.length && 'No places found within these walking limits. Increase the limits or choose on the map.'}
      </div>
      <div className="meeting-picker-venues">
        {venues.map((venue, index) => <button key={venue.location_id} type="button" className="meeting-picker-venue" aria-pressed={String(selectedLocation?.location_id) === String(venue.location_id)} onClick={() => selectVenue(venue)}>
          <strong>{index + 1}. {venue.name}</strong><span>{venue.venue_type}</span>
          <span>You: {distance(youAreWalker ? venue.walker_distance_miles : venue.dog_owner_distance_miles, youAreWalker ? venue.walker_walking_minutes : venue.dog_owner_walking_minutes)}</span>
          <span>{partnerName}: {distance(youAreWalker ? venue.dog_owner_distance_miles : venue.walker_distance_miles, youAreWalker ? venue.dog_owner_walking_minutes : venue.walker_walking_minutes)}</span>
          <span>{String(selectedLocation?.location_id) === String(venue.location_id) ? 'Selected' : 'Select this place'}</span>
        </button>)}
      </div>
      {participants && <div className="meeting-picker-direct">
        <button type="button" disabled={directStatus === 'loading'} onClick={loadDirect}>{directStatus === 'loading' ? 'Checking walking distance…' : 'Walking distance between your profiles'}</button>
        {direct && <p>{distance(direct.distance_miles, direct.estimated_walking_minutes)}. Choose a meeting point below to continue.</p>}
        {directStatus === 'error' && <p role="status">Walking distance is unavailable. You can still select a meeting place.</p>}
      </div>}
    </>}
    {mode === 'manual' && <>
      <form className="meeting-picker-search" onSubmit={(event) => { event.preventDefault(); if (address.trim()) geocode({ address: address.trim() }); }}>
        <label>Search a meeting address<input value={address} onChange={(event) => setAddress(event.target.value)} placeholder="Street, park or café and city" required /></label>
        <button type="submit" disabled={!isMapLoaded}>Find address</button>
      </form>
      <button type="button" onClick={useLocation} disabled={locating || !isMapLoaded}>{locating ? 'Finding your location…' : 'Use my current location'}</button>
      <label className="meeting-picker-nearby">Nearby places<select value="" onChange={(event) => { const venue = nearby[Number(event.target.value)]; if (venue) selectVenue(venue); }}>
        <option value="" disabled>Choose a nearby place</option>{nearby.map((venue, index) => <option key={index} value={index}>{venue.name}</option>)}
      </select></label>
      <p role="status">{nearbyStatus === 'loading' ? 'Finding nearby places…' : nearbyStatus === 'error' ? 'Nearby places could not load. Search an address or tap the map.' : nearbyStatus === 'ready' && !nearby.length ? 'No nearby places found. Search an address or tap the map.' : ''}</p>
      {nearbyStatus === 'error' && <button type="button" onClick={() => setRetry((value) => value + 1)}>Retry nearby places</button>}
    </>}
    <p className="meeting-picker-note">Tap the map to choose a different meeting point.</p>
    <div className="meeting-picker-map">
      {isMapLoaded && mapCenter ? <GoogleMap mapContainerStyle={{ width: '100%', height: '100%' }} center={mapCenter} zoom={15} options={{ disableDefaultUI: true, zoomControl: true, gestureHandling: 'cooperative' }} onClick={(event) => geocode({ location: { lat: event.latLng.lat(), lng: event.latLng.lng() } })}>
        {displayed.map((venue, index) => <MarkerF key={venue.location_id || index} position={coordinates(venue)} label={String(index + 1)} title={venue.name} onClick={() => selectVenue(venue)} />)}
        {coordinates(selectedLocation) && <MarkerF position={coordinates(selectedLocation)} title={selectedAddress || 'Selected meeting point'} />}
      </GoogleMap> : <p>{!isMapLoaded ? 'Loading map…' : 'Search an address or use your location to open the map.'}</p>}
    </div>
    {addressStatus && <p role="status">{addressStatus}</p>}
    {!selectedAddress && <p className="meeting-picker-note">Select a meeting place to continue.</p>}
    {selectedAddress && <p className="meeting-picker-selection"><strong>Meeting point:</strong> {selectedAddress}</p>}
  </div>;
}
